import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { Routes } from '../../classes/Routes';
import Alert from '../../components/UI/Alert/Alert';
import Button from '../../components/UI/Button/Button';
import Container from '../../components/UI/Container/Container';
import  { EInputType, IInputField } from '../../components/UI/Input/Input';
import { useInputs } from '../../components/UI/Input/useInputs';
import InputGroup from '../../components/UI/InputGroup/InputGroup';
import Spinner from '../../components/UI/Spinner/Spinner';
import { EInputUpdateAction } from '../../context/InputContext';
import { invoicingTypes } from '../../data/select-data';
import { EInvoicingType, EOrganizationLevel } from '../../enums';
import { useAuthUser } from '../../hooks/useAuthUser';
import { IAppState, IOrganization, IOrganizationState } from '../../interfaces';
import { countries } from '../../shared/countries';
import {  initForm } from '../../shared/utility';
import * as actions from '../../store/actions';
import { ETranslation } from '../../translations/translation-keys';
import classes from './Organization.module.scss';

interface IProps extends RouteComponentProps {}

export enum EOrganization {
  name = "name",
  businessId = "businessId",
  streetAddress = "streetAddress",
  zip = "zip",
  city = "city",
  countryCode = "countryCode",
  operatorToken = "operatorToken",
  latitude = "latitude",
  longitude = "longitude",
  phone = "phone",
  logoBase64Text = "logoBase64Text",
  invoicingType = "invoicingType",
  eInvoiceAddress = "eInvoiceAddress",
  eInvoiceOperatorAddress = "eInvoiceOperatorAddress",
  invoicingInfo = "invoicingInfo",
  services = "services",
  locations = "locations",
  created = "created",
  linkWWW = "linkWWW",
  linkFacebook = "linkFacebook",
  linkTwitter = "linkTwitter",
  linkInstagram = "linkInstagram",
  linkLinkedin = "linkLinkedin",
  aiCompanyName = "aiCompanyName",
  aiContactName = "aiContactName",
  aiEmail = "aiEmail",
  aiPhone = "aiPhone",
  aiInstructions = "aiInstructions",
  aiFooter = "aiFooter",
  partnerDomains = "partnerDomains",
  operatingRadius = "operatingRadius",
}

const Organization: React.FC<IProps> = ({ history }) => {
  const dispatch = useDispatch();

  const { loading, error, organization } = useSelector<
    IAppState,
    IOrganizationState
  >((state) => state.organization);

  const authUser = useAuthUser();

  const { t } = useTranslation();

  const [inputs, setInputs] = useState<IInputField>({
    [EOrganization.name]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OPERATOR_NAME,
      placeholderTranslation: ETranslation.OPERATOR_NAME,
      value: "",
    },
    [EOrganization.businessId]: {
      type: EInputType.text,
      labelTranslation: ETranslation.OPERATOR_BUSINESS_ID,
      placeholderTranslation: ETranslation.OPERATOR_BUSINESS_ID,
      value: "",
    },
    [EOrganization.streetAddress]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_STREET_ADDRESS,
      placeholderTranslation: ETranslation.COMMON_STREET_ADDRESS,
      value: "",
    },
    [EOrganization.zip]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_ZIP,
      placeholderTranslation: ETranslation.COMMON_ZIP,
      value: "",
    },
    [EOrganization.city]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_CITY,
      placeholderTranslation: ETranslation.COMMON_CITY,
      value: "",
    },
    [EOrganization.countryCode]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.COMMON_COUNTRY,
      placeholderTranslation: ETranslation.COMMON_COUNTRY,
      value: "",
      options: countries,
    },
    [EOrganization.operatorToken]: {
      type: EInputType.static,
      labelTranslation: ETranslation.COMMON_TOKEN,
      value: "",
    },
    [EOrganization.latitude]: {
      type: EInputType.number,
      labelTranslation: ETranslation.LOCATION_LATITUDE,
      placeholderTranslation: ETranslation.LOCATION_LATITUDE,
      value: "",
    },
    [EOrganization.longitude]: {
      type: EInputType.number,
      labelTranslation: ETranslation.LOCATION_LONGITUDE,
      placeholderTranslation: ETranslation.LOCATION_LONGITUDE,
      value: "",
    },
    [EOrganization.phone]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_PHONE,
      placeholderTranslation: ETranslation.COMMON_PHONE,
      value: "",
    },
    [EOrganization.logoBase64Text]: {
      type: EInputType.organizationImage,
      labelTranslation: ETranslation.ORGANIZATION_IMAGE,
      placeholderTranslation: ETranslation.ORGANIZATION_IMAGE,
      value: "",
    },
    [EOrganization.invoicingType]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.SERVICEPROVIDER_INVOICING_TYPE,
      placeholderTranslation: ETranslation.SERVICEPROVIDER_INVOICING_TYPE,
      value: EInvoicingType.EINVOICE,
      options: invoicingTypes,
      validation: {
        required: true,
      },
    },
    [EOrganization.eInvoiceAddress]: {
      type: EInputType.text,
      labelTranslation: ETranslation.SERVICEPROVIDER_EINVOICE_ADDRESS,
      placeholderTranslation: ETranslation.SERVICEPROVIDER_EINVOICE_ADDRESS,
      value: "",
    },
    [EOrganization.eInvoiceOperatorAddress]: {
      type: EInputType.text,
      labelTranslation: ETranslation.SERVICEPROVIDER_EINVOICEOPERATOR_ADDRESS,
      placeholderTranslation:
        ETranslation.SERVICEPROVIDER_EINVOICEOPERATOR_ADDRESS,
      value: "",
    },
    [EOrganization.invoicingInfo]: {
      type: EInputType.textarea,
      labelTranslation: ETranslation.SERVICEPROVIDER_INVOICEINFO,
      placeholderTranslation: ETranslation.SERVICEPROVIDER_INVOICEINFO,
      value: "",
    },
    [EOrganization.services]: {
      type: EInputType.static,
      labelTranslation: ETranslation.SERVICES,
      value: "",
    },
    [EOrganization.locations]: {
      type: EInputType.static,
      labelTranslation: ETranslation.LOCATIONS,
      value: "",
    },
    [EOrganization.created]: {
      type: EInputType.static,
      labelTranslation: ETranslation.ORGANIZATION_ORDER_DATE,
      value: "",
    },
    [EOrganization.linkWWW]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_WWW,
      placeholderTranslation: ETranslation.COMMON_WWW,
      value: "",
      validation: { isUrl: true },
    },
    [EOrganization.linkFacebook]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_FACEBOOK,
      placeholderTranslation: ETranslation.COMMON_FACEBOOK,
      value: "",
      validation: { isUrl: true, urlDomains: ["facebook.com", "fb.com"] },
    },
    [EOrganization.linkTwitter]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_TWITTER,
      placeholderTranslation: ETranslation.COMMON_TWITTER,
      value: "",
      validation: { isUrl: true, urlDomains: ["twitter.com", "x.com"] },
    },
    [EOrganization.linkInstagram]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_INSTAGRAM,
      placeholderTranslation: ETranslation.COMMON_INSTAGRAM,
      value: "",
      validation: { isUrl: true, urlDomains: ["instagram.com"] },
    },
    [EOrganization.linkLinkedin]: {
      type: EInputType.text,
      labelTranslation: ETranslation.COMMON_LINKEDIN,
      placeholderTranslation: ETranslation.COMMON_LINKEDIN,
      value: "",
      validation: { isUrl: true, urlDomains: ["linkedin.com"] },
    },
    [EOrganization.aiCompanyName]: {
      type: EInputType.text,
      labelTranslation: ETranslation.ORGANIZATION_AI_COMPANY_NAME,
      placeholderTranslation: ETranslation.ORGANIZATION_AI_COMPANY_NAME,
      value: "",
    },
    [EOrganization.aiContactName]: {
      type: EInputType.text,
      labelTranslation: ETranslation.ORGANIZATION_AI_CONTACT_NAME,
      placeholderTranslation: ETranslation.ORGANIZATION_AI_CONTACT_NAME,
      value: "",
    },
    [EOrganization.aiEmail]: {
      type: EInputType.text,
      labelTranslation: ETranslation.ORGANIZATION_AI_EMAIL,
      placeholderTranslation: ETranslation.ORGANIZATION_AI_EMAIL,
      value: "",
    },
    [EOrganization.aiPhone]: {
      type: EInputType.text,
      labelTranslation: ETranslation.ORGANIZATION_AI_PHONE,
      placeholderTranslation: ETranslation.ORGANIZATION_AI_PHONE,
      value: "",
    },
    [EOrganization.aiInstructions]: {
      type: EInputType.textarea,
      labelTranslation: ETranslation.ORGANIZATION_AI_INSTRUCTIONS,
      placeholderTranslation: ETranslation.ORGANIZATION_AI_INSTRUCTIONS,
      value: "",
      rows: 6
    },
    [EOrganization.aiFooter]: {
      type: EInputType.textarea,
      labelTranslation: ETranslation.ORGANIZATION_AI_FOOTER,
      placeholderTranslation: ETranslation.ORGANIZATION_AI_FOOTER,
      value: "",
      rows: 6
    },
    [EOrganization.partnerDomains]: {
      type: EInputType.textarea,
      labelTranslation: ETranslation.ORGANIZATION_PARTNER_DOMAINS,
      placeholderTranslation: ETranslation.ORGANIZATION_PARTNER_DOMAINS_PLACEHOLDER,
      value: "",
      rows: 3
    },
    [EOrganization.operatingRadius]: {
      type: EInputType.number,
      labelTranslation: ETranslation.SERVICEPROVIDER_OPERATING_RADIUS,
      placeholderTranslation: ETranslation.SERVICEPROVIDER_OPERATING_RADIUS,
      value: "",
    },
  });

  const [activeTab, setActiveTab] = useState<string>("perustiedot");
  const [servicePrompts, setServicePrompts] = useState<Record<string, string>>({});
  const [customizedServices, setCustomizedServices] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (history.location.hash === "#ai-tarjous") {
      setActiveTab("ai-tarjous");
    } else {
      setActiveTab("perustiedot");
    }
  }, [history.location.hash]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    history.push({ hash: tab });
  };

  useEffect(() => {
    if (authUser?.organization?.id) {
      dispatch(actions.getMyOrganization());
    }
    return () => {
      dispatch(actions.clearOrganization());
    }
  }, [dispatch, authUser]);

  useEffect(() => {
    if (organization) {
      const initData = { ...organization };
      if (!initData.aiCompanyName) initData.aiCompanyName = initData.name;
      if (!initData.aiContactName) initData.aiContactName = authUser?.name || '';
      if (!initData.aiEmail) initData.aiEmail = authUser?.email || '';
      if (!initData.aiPhone) initData.aiPhone = initData.phone || '';
      
      let parsedPrompts: Record<string, string> = {};
      let initialCustomized: Record<string, boolean> = {};
      if (initData.aiServicePrompts) {
        try {
          if (typeof initData.aiServicePrompts === 'string') {
             parsedPrompts = JSON.parse(initData.aiServicePrompts);
          } else {
             parsedPrompts = initData.aiServicePrompts as any;
          }
        } catch(e) {}
      }
      
      if (initData.services) {
        initData.services.forEach(service => {
          if (service.serviceToken) {
            const savedPrompt = parsedPrompts[service.serviceToken];
            const isCustom = savedPrompt !== undefined && savedPrompt !== null && savedPrompt !== '' && savedPrompt !== service.aiPrompt;
            initialCustomized[service.serviceToken] = isCustom;
            
            if (savedPrompt === undefined) {
               parsedPrompts[service.serviceToken] = service.aiPrompt || "";
            }
          }
        });
      }
      setServicePrompts(parsedPrompts);
      setCustomizedServices(initialCustomized);
      
      initForm(setInputs, initData);
    }
  }, [organization, authUser]);

  const { createInput, submit } = useInputs({
    inputs,
    setInputs,
    options: {
      updateAction: EInputUpdateAction.ORGANIZATION
    }
  });

  const submitHandler = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();

    const organizationData = await submit() as IOrganization;
    if (!organizationData) return;

    if (organizationData.operatingRadius !== undefined && organizationData.operatingRadius !== null && (organizationData.operatingRadius as any) !== "") {
      organizationData.operatingRadius = Number(organizationData.operatingRadius);
    } else {
      organizationData.operatingRadius = undefined;
    }

    const savedServicePrompts: Record<string, string> = {};
    if (organization?.services) {
      organization.services.forEach(service => {
        if (service.serviceToken && customizedServices[service.serviceToken]) {
          savedServicePrompts[service.serviceToken] = servicePrompts[service.serviceToken] || "";
        }
      });
    }

    organizationData.aiServicePrompts = JSON.stringify(savedServicePrompts);

    try {
      await dispatch(actions.updateMyOrganization(organizationData) as any);
      history.push(Routes.OFFER_REQUESTS);
    } catch (e) {
      console.error("Failed to update organization", e);
    }
  };

  const createStaticContent = (title: string, content?: string) => (
    <div className={classes.StaticContent}>
      <label>{title}</label>
      <p>{content}</p>
    </div>
  )

  return (
    <>
      {error && <Alert children={error} />}
      <Container style={{ maxWidth: 1200, margin: '1rem auto' }}>
        <h2>{t(ETranslation.ORGANIZATION_ME)}</h2>
        {loading ? (
          <Spinner />
        ) : (
          <>
            <div style={{ display: 'flex', borderBottom: '1px solid #ccc', marginBottom: '2rem', gap: '1rem' }}>
              <div 
                style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: activeTab === 'perustiedot' ? '2px solid #0d6efd' : '2px solid transparent', fontWeight: activeTab === 'perustiedot' ? 'bold' : 'normal', color: activeTab === 'perustiedot' ? '#0d6efd' : '#555' }}
                onClick={() => handleTabChange('perustiedot')}
              >
                Perustiedot
              </div>
              <div 
                style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: activeTab === 'ai-tarjous' ? '2px solid #0d6efd' : '2px solid transparent', fontWeight: activeTab === 'ai-tarjous' ? 'bold' : 'normal', color: activeTab === 'ai-tarjous' ? '#0d6efd' : '#555' }}
                onClick={() => handleTabChange('ai-tarjous')}
              >
                AI tarjous
              </div>
            </div>

            {activeTab === 'perustiedot' && (
              <>
            <h3 style={{ marginTop: '0', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Perustiedot</h3>
            <InputGroup>
              {createInput(EOrganization.name)}
              {createInput(EOrganization.businessId)}
            </InputGroup>
            
            <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Yhteystiedot</h3>
            <InputGroup>
              {createInput(EOrganization.streetAddress)}
              {createInput(EOrganization.countryCode)}
            </InputGroup>
            <InputGroup>
              {createInput(EOrganization.zip)}
              {createInput(EOrganization.city)}
            </InputGroup>
            {(organization?.level === EOrganizationLevel.SERVICE_PROVIDER || authUser?.isServiceProvider) && (
              <>
                <InputGroup>
                  {createInput(EOrganization.latitude)}
                  {createInput(EOrganization.longitude)}
                </InputGroup>
                <InputGroup>
                  {createInput(EOrganization.operatingRadius)}
                  <div />
                </InputGroup>
                <InputGroup>
                  {createInput(EOrganization.phone)}
                  {createInput(EOrganization.linkWWW)}
                </InputGroup>

                <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Brändi ja Sosiaalinen media</h3>
                {createInput(EOrganization.logoBase64Text)}
                <InputGroup>
                  {createInput(EOrganization.linkFacebook)}
                  {createInput(EOrganization.linkInstagram)}
                </InputGroup>
                <InputGroup>
                  {createInput(EOrganization.linkLinkedin)}
                  {createInput(EOrganization.linkTwitter)}
                </InputGroup>

                <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Laskutustiedot</h3>
                {createInput(EOrganization.invoicingType)}
                {inputs[EOrganization.invoicingType].value ===
                  EInvoicingType.EINVOICE && (
                  <InputGroup>
                    {createInput(EOrganization.eInvoiceAddress)}
                    {createInput(EOrganization.eInvoiceOperatorAddress)}
                  </InputGroup>
                )}
                {createInput(EOrganization.invoicingInfo)}      

                <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Järjestelmätiedot</h3>
                {createStaticContent(t(ETranslation.SERVICES), organization?.services?.map(service => service.name).join(", "))}
                {createStaticContent(t(ETranslation.LOCATIONS), organization?.locations?.map(location => location.name).join(", "))}
                {createInput(EOrganization.created)}
              </>
            )}
            {(organization?.level === EOrganizationLevel.OPERATOR || organization?.level === EOrganizationLevel.PRIMARY) && (
              <>
                <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Kumppanit</h3>
                {createInput(EOrganization.partnerDomains)}
              </>
            )}
              </>
            )}

            {activeTab === 'ai-tarjous' && (
              <>
                <h3 style={{ marginTop: '0', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>AI-vastausehdotuksen tiedot</h3>
                <p style={{ marginBottom: '1.5rem', color: '#555' }}>
                  Näitä tietoja käytetään pohjana, kun tekoäly luo valmiita tarjousehdotuksia saapuneisiin tarjouspyyntöihin.
                  Jos kentät jätetään tyhjiksi, tekoäly käyttää mahdollisuuksien mukaan yrityksesi ja käyttäjäsi perustietoja.
                </p>
                <InputGroup>
                  {createInput(EOrganization.aiCompanyName)}
                  {createInput(EOrganization.aiContactName)}
                </InputGroup>
                <InputGroup>
                  {createInput(EOrganization.aiEmail)}
                  {createInput(EOrganization.aiPhone)}
                </InputGroup>
                {createInput(EOrganization.aiInstructions)}
                {createInput(EOrganization.aiFooter)}

                {organization?.services && organization.services.length > 0 && (
                  <>
                    <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Palvelukohtaiset erikoisohjeet (Service Prompts)</h3>
                    <p style={{ marginBottom: '1.5rem', color: '#555' }}>
                      Voit määrittää erilliset ohjeet eri palveluille. Näihin on esitäytetty pääkäyttäjän asettama oletusohje, jota voit muokata vapaasti ottamalla oman version käyttöön.
                    </p>
                    {organization.services.map(service => {
                      if (!service.serviceToken) return null;
                      const isCustomized = customizedServices[service.serviceToken] || false;
                      return (
                        <div key={service.serviceToken} className={classes.ServicePromptCard}>
                          <div className={classes.ServiceHeader}>
                            <h4>{service.name}</h4>
                            <label className={classes.ToggleContainer}>
                              <span className={classes.ToggleLabel}>Muokkaa omaa versiota</span>
                              <span className={classes.Switch}>
                                <input
                                  type="checkbox"
                                  checked={isCustomized}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setCustomizedServices(prev => ({ ...prev, [service.serviceToken!]: checked }));
                                    if (checked) {
                                      setServicePrompts(prev => {
                                        if (!prev[service.serviceToken!] || prev[service.serviceToken!].trim() === '') {
                                          return { ...prev, [service.serviceToken!]: service.aiPrompt || "" };
                                        }
                                        return prev;
                                      });
                                    }
                                  }}
                                />
                                <span className={classes.Slider} />
                              </span>
                            </label>
                          </div>
                          {isCustomized ? (
                            <textarea
                              className={classes.CustomTextarea}
                              value={(service.serviceToken && servicePrompts[service.serviceToken]) || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (service.serviceToken) {
                                  setServicePrompts(prev => ({ ...prev, [service.serviceToken!]: val }));
                                }
                              }}
                              placeholder="Kirjoita tähän palvelukohtainen tekoälyn ohjeistus..."
                            />
                          ) : (
                            <div className={classes.DefaultPromptContainer}>
                              {service.aiPrompt || "Ei oletusohjetta asetettu."}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </>
        )}
        <InputGroup>
          <Button disabled={loading} onClick={submitHandler}>
            {t(ETranslation.COMMON_SAVE)}
          </Button>
          <Button onClick={() => history.goBack()}>
            {t(ETranslation.COMMON_RETURN)}
          </Button>
        </InputGroup>
      </Container>
    </>
  );
};

export default Organization;
